import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Mail, Phone, Send, Loader2 } from "lucide-react";
import { useLanguage } from "@/i18n";
import { Reveal, Chapter } from "./shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl border border-neutral-200 bg-white px-4 py-3 text-sm text-neutral-900 outline-none transition-[border-color,box-shadow] duration-300 placeholder:text-neutral-400 focus:border-brand-600 focus:ring-4 focus:ring-brand-600/10";

export const Contact = () => {
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", fleet_size: "", vehicle_interest: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success(t.contact.success);
      setForm({ name: "", company: "", email: "", phone: "", fleet_size: "", vehicle_interest: "", message: "" });
    } catch {
      toast.error(t.contact.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="scroll-mt-24 bg-paper" data-testid="contact-section">
      <div className="mx-auto max-w-7xl px-5 py-24 lg:px-10 lg:py-32">
        <Chapter num="08" label={t.contact.chapter.split("/ ")[1]} />
        <div className="mt-12 grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <h2 data-testid="contact-heading" className="font-display text-3xl font-extrabold tracking-tight text-neutral-950 sm:text-4xl lg:text-5xl">
                {t.contact.heading}
              </h2>
              <p className="mt-4 text-base text-neutral-500 sm:text-lg">{t.contact.sub}</p>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="mt-10 space-y-7">
                <div data-testid="contact-address">
                  <div className="flex items-center gap-2 font-mono text-[11px] tracking-[0.25em] text-brand-700"><MapPin size={13} /> {t.contact.addressLabel}</div>
                  <p className="mt-2 text-sm leading-relaxed text-neutral-700">
                    electro.technology sp. z o. o.<br />Jasnogórska 1, 31-358 Kraków, <strong>POLAND</strong>
                  </p>
                </div>
                <div data-testid="contact-email">
                  <div className="flex items-center gap-2 font-mono text-[11px] tracking-[0.25em] text-brand-700"><Mail size={13} /> {t.contact.emailLabel}</div>
                  <a href="mailto:kontakt@amperi.eu?subject=electro.technology" className="mt-2 inline-block text-sm text-neutral-700 underline decoration-brand-200 underline-offset-4 transition-colors hover:text-brand-700">
                    kontakt@amperi.eu
                  </a>
                </div>
                <div data-testid="contact-phone">
                  <div className="flex items-center gap-2 font-mono text-[11px] tracking-[0.25em] text-brand-700"><Phone size={13} /> {t.contact.phoneLabel}</div>
                  <div className="mt-2 flex flex-col gap-1">
                    <a href="tel:+48888822112" className="text-sm text-neutral-700 transition-colors hover:text-brand-700">+48 <strong>8888 22 112</strong></a>
                    <a data-testid="contact-phone-2" href="tel:+48123964554" className="text-sm text-neutral-700 transition-colors hover:text-brand-700">+48 <strong>12 396 45 54</strong></a>
                  </div>
                </div>
                <div data-testid="contact-socials">
                  <div className="font-mono text-[11px] tracking-[0.25em] text-brand-700">{t.contact.socialsLabel}</div>
                  <div className="mt-2 flex gap-4 text-sm">
                    <a data-testid="social-facebook" href="https://www.facebook.com/pojazdyamperi" target="_blank" rel="noopener noreferrer" className="text-neutral-600 underline decoration-brand-200 underline-offset-4 hover:text-brand-700">Facebook</a>
                    <a data-testid="social-linkedin" href="https://www.linkedin.com/company/77858167" target="_blank" rel="noopener noreferrer" className="text-neutral-600 underline decoration-brand-200 underline-offset-4 hover:text-brand-700">LinkedIn</a>
                    <a data-testid="social-instagram" href="https://www.instagram.com/skutery.elektryczne/" target="_blank" rel="noopener noreferrer" className="text-neutral-600 underline decoration-brand-200 underline-offset-4 hover:text-brand-700">Instagram</a>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.12} className="lg:col-span-7">
            <form onSubmit={submit} data-testid="contact-form" className="rounded-[1.75rem] border border-neutral-200 bg-white p-7 shadow-[0_30px_70px_-40px_rgba(44,56,28,0.35)] sm:p-10">
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="enquiry-name" className="mb-1.5 block text-xs font-semibold text-neutral-600">{t.contact.name} *</label>
                  <input id="enquiry-name" data-testid="enquiry-name-input" required value={form.name} onChange={set("name")} className={inputCls} />
                </div>
                <div>
                  <label htmlFor="enquiry-company" className="mb-1.5 block text-xs font-semibold text-neutral-600">{t.contact.company}</label>
                  <input id="enquiry-company" data-testid="enquiry-company-input" value={form.company} onChange={set("company")} className={inputCls} />
                </div>
                <div>
                  <label htmlFor="enquiry-email" className="mb-1.5 block text-xs font-semibold text-neutral-600">{t.contact.email} *</label>
                  <input id="enquiry-email" data-testid="enquiry-email-input" type="email" required value={form.email} onChange={set("email")} className={inputCls} />
                </div>
                <div>
                  <label htmlFor="enquiry-phone" className="mb-1.5 block text-xs font-semibold text-neutral-600">{t.contact.phone}</label>
                  <input id="enquiry-phone" data-testid="enquiry-phone-input" value={form.phone} onChange={set("phone")} className={inputCls} />
                </div>
                <div>
                  <label htmlFor="enquiry-fleetsize" className="mb-1.5 block text-xs font-semibold text-neutral-600">{t.contact.fleetSize}</label>
                  <select id="enquiry-fleetsize" data-testid="enquiry-fleetsize-select" value={form.fleet_size} onChange={set("fleet_size")} className={inputCls}>
                    <option value="">—</option>
                    {t.contact.fleetOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
                <div>
                  <label htmlFor="enquiry-vehicle" className="mb-1.5 block text-xs font-semibold text-neutral-600">{t.contact.vehicle}</label>
                  <select id="enquiry-vehicle" data-testid="enquiry-vehicle-select" value={form.vehicle_interest} onChange={set("vehicle_interest")} className={inputCls}>
                    <option value="">—</option>
                    {t.contact.vehicleOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="enquiry-message" className="mb-1.5 block text-xs font-semibold text-neutral-600">{t.contact.message} *</label>
                  <textarea id="enquiry-message" data-testid="enquiry-message-input" required rows={4} value={form.message} onChange={set("message")} className={`${inputCls} resize-none`} />
                </div>
              </div>
              <button
                type="submit"
                data-testid="contact-form-submit-button"
                disabled={sending}
                className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand-600 px-8 py-4 text-sm font-semibold text-white transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-brand-700 disabled:opacity-60 sm:w-auto"
              >
                {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                {sending ? t.contact.sending : t.contact.submit}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
